import React, { useEffect, useState, useMemo } from "react";
import { Tabs, Button, Switch, Modal, InputNumber, Input, Table } from "antd";
import { SaveOutlined, PrinterOutlined, ZoomOutOutlined, SettingOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useData } from '../../contexts/DataContext';

const { TabPane } = Tabs;

const GraphData = () => {
  const { sites, selectedSites, markers } = useData();
  const [selectedSeriesData, setSelectedSeriesData] = useState([]);
  const [series, setSeries] = useState([
    {
      name: "Raw",
      data: [],
      color: "#7c2975"
    },
  ]);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [currentSettings, setCurrentSettings] = useState({
    lineWidth: 2,
    markerSize: 2,
    color: '#7c2975',
  });
  const [tempSettings, setTempSettings] = useState(currentSettings);
  const [chartType, setChartType] = useState("line");

  useEffect(() => {
    if (!series || !sites || !markers) {
      return;
    }
    let newSeries = markers.map(timeSeries => {
      let sT = new Date(timeSeries.from_time);
      let eT = new Date(timeSeries.to_time);
      let temp = series.filter((d) => d[0] >= sT && d[0] <= eT);

      return {
        name: timeSeries.action,
        data: temp.map(d => ({ time: d[0], value: d[1] })),
        color: getColorByName(timeSeries.action)
      };
    });

    newSeries.unshift({
      name: 'Raw',
      data: [],
      color: getColorByName('Raw')
    });

    setSeries(newSeries);
  }, [sites, markers]);

  function getColorByName(name) {
    switch (name) {
      case 'Raw':
        return '#FF0000';
      case 'action 1':
        return '#00FF00';
      case 'action 2':
        return '#0000FF';
      case 'action 3':
        return '#FFFF00';
      default:
        return '#000000';
    }
  }

  const handleChartTypeChange = (checked) => {
    setChartType(checked ? 'scatter' : 'line');
  };

  const handleOpenSettings = () => {
    setTempSettings(currentSettings);
    setIsSettingsModalVisible(true);
  };

  const handleSettingsOk = () => {
    setCurrentSettings(tempSettings);
    setIsSettingsModalVisible(false);
  };

  const handleSettingsCancel = () => {
    setIsSettingsModalVisible(false);
  };

  const handleSettingsChange = (field, value) => {
    setTempSettings(prevSettings => ({ ...prevSettings, [field]: value }));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'timeseriesName',
      key: 'timeseriesName',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Data Approval Date',
      dataIndex: 'correctionDataApproval',
      key: 'correctionDataApproval',
    },
    {
      title: 'Grade',
      dataIndex: 'grade',
      key: 'grade',
    },
    {
      title: 'Field Notes',
      dataIndex: 'fieldNotes',
      key: 'fieldNotes',
    },
  ];

  return (
    <div className="w-full">
      <div className="flex flex-col space-y-4">
        {/* Toolbar */}
        <div className="flex items-center space-x-4">
          <Button
            icon={<SettingOutlined />}
            type="link"
            className="flex items-center"
            onClick={handleOpenSettings}
          >
            <span className="ml-2">Settings</span>
          </Button>
          <Button
            icon={<ZoomOutOutlined />}
            type="link"
            className="flex items-center"
          >
            <span className="ml-2">Reset Zoom</span>
          </Button>
          <Button
            icon={<PrinterOutlined />}
            type="link"
            className="flex items-center"
          >
            <span className="ml-2">Print</span>
          </Button>
          <Button
            icon={<SaveOutlined />}
            type="link"
            className="flex items-center"
          >
            <span className="ml-2">Save As</span>
          </Button>
          <Switch 
            checkedChildren="Scatter" 
            unCheckedChildren="Line" 
            onChange={handleChartTypeChange} 
            checked={chartType === 'scatter'}
            className="w-13"
            />
        </div>

        {/* Main Chart */}
        <div className="bg-white rounded shadow p-4">
          {series.length > 0 && (
            <ResponsiveContainer width="100%" height={600}>
              <LineChart data={series[0].data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  tickFormatter={(time) =>
                    new Date(time).toLocaleTimeString('en-US')
                  }
                />
                <YAxis />
                <Tooltip />
                <Legend />
                {series.map((s) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey="value"
                    data={s.data}
                    stroke={s.color}
                    strokeWidth={currentSettings.lineWidth}
                    dot={{ r: currentSettings.markerSize }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Data Table */}
        <div className="bg-white rounded shadow">
          <Table columns={columns} dataSource={selectedSeriesData} />
        </div>
      </div>

      {/* Settings Modal */}
      <Modal
        title="Settings"
        visible={isSettingsModalVisible}
        onOk={handleSettingsOk}
        onCancel={handleSettingsCancel}
      >
        <div className="space-y-4">
          <div className="flex items-center">
            <label className="mr-2">Color:</label>
            <Input
              type="color"
              value={tempSettings.color}
              onChange={(e) => handleSettingsChange('color', e.target.value)}
            />
          </div>
          <div className="flex items-center">
            <label className="mr-2">Line Width:</label>
            <InputNumber
              min={1}
              max={10}
              value={tempSettings.lineWidth}
              onChange={(value) => handleSettingsChange('lineWidth', value)}
            />
          </div>
          <div className="flex items-center">
            <label className="mr-2">Marker Size:</label>
            <InputNumber
              min={1}
              max={10}
              value={tempSettings.markerSize}
              onChange={(value) => handleSettingsChange('markerSize', value)}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GraphData;
